import { useNavigate } from 'react-router-dom'
import { ArrowRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { CefrBadge } from '@/components/CefrBadge'
import type { UnderstandResponse } from '@/types/content'
import {
  landingDemoContextFor,
  landingDemoSentenceFor,
} from '@/lib/landingDemoFixtures'
import { useGuestLocale, useLandingCopy } from '@/hooks/useGuestLocale'
import { trackFunnel } from '@/lib/funnel'
import { cn } from '@/components/ui/cn'

interface LandingDemoPanelProps {
  understand: UnderstandResponse
  isPreview?: boolean
  className?: string
}

export function LandingDemoPanel({
  understand,
  isPreview = false,
  className,
}: LandingDemoPanelProps) {
  const copy = useLandingCopy()
  const locale = useGuestLocale()
  const navigate = useNavigate()
  const popup = understand.wordDetails
  const primarySense = popup?.localizedDefinitions.find((d) => d.isPrimary)
    ?? popup?.localizedDefinitions[0]
  const contextGloss = landingDemoContextFor(understand.selection, locale)
  const contextSentence = landingDemoSentenceFor(understand.selection)

  if (!popup || !primarySense) return null

  return (
    <div
      className={cn('flex h-full flex-col animate-in fade-in slide-in-from-bottom-2 duration-300', className)}
      data-testid="landing-demo-panel"
    >
      {isPreview && (
        <p className="mb-3 text-xs text-muted-foreground">{copy.demoTapHint}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xl font-semibold text-foreground">{understand.selection}</span>
        {popup.phonetic && (
          <span className="text-xs text-muted-foreground">{popup.phonetic}</span>
        )}
        {popup.cefrLevel && <CefrBadge level={popup.cefrLevel} />}
        {primarySense.partOfSpeech && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs italic text-muted-foreground">
            {primarySense.partOfSpeech}
          </span>
        )}
      </div>

      <div className="mt-4" data-testid="popup-translation">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {copy.demoMeans}
        </p>
        <p className="mt-1 text-lg font-semibold leading-snug text-foreground">
          {primarySense.translation}
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {primarySense.englishGloss}
        </p>
      </div>

      {contextSentence && contextGloss && (
        <div className="mt-4 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5" data-testid="popup-localized-definitions">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {copy.demoInContext}
          </p>
          <p className="mt-1.5 text-sm italic leading-relaxed text-foreground/90">
            &ldquo;{contextSentence}&rdquo;
          </p>
          <p className="mt-2 text-sm leading-relaxed text-foreground">
            → {contextGloss}
          </p>
        </div>
      )}

      {popup.examples[0] && (
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
          {copy.demoExampleLabel}: &ldquo;{popup.examples[0]}&rdquo;
        </p>
      )}

      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        {copy.demoLocalizedNote}
      </p>

      <div className="mt-auto pt-4">
        <Button
          className="w-full border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
          onClick={() => {
            trackFunnel('REGISTER_CLICK', { source: 'demo_panel' })
            navigate('/register')
          }}
          data-testid="popup-add-word"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {copy.signUpToSave}
        </Button>
      </div>
    </div>
  )
}

function LandingDemoPlaceholder() {
  const copy = useLandingCopy()
  return (
    <div
      className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-8 text-center lg:min-h-0"
      data-testid="landing-demo-placeholder"
    >
      <ArrowRight className="mb-2 h-5 w-5 text-primary" aria-hidden />
      <p className="text-sm font-medium text-foreground">{copy.demoTapHint}</p>
    </div>
  )
}

export function LandingDemoSidePanel({
  understand,
  isPreview = false,
}: {
  understand: UnderstandResponse | null
  isPreview?: boolean
}) {
  if (!understand) {
    return <LandingDemoPlaceholder />
  }
  return <LandingDemoPanel understand={understand} isPreview={isPreview} />
}
